import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface FileInputValidators {
  allowedTypes?: string[];        // e.g. ['image/png', 'image/jpeg']
  maxFileSizeBytes?: number;      // per file
  maxTotalSizeBytes?: number;     // all files together
}

@Component({
    selector: 'ufla-file-upload',
    standalone: true,
    imports: [
        DecimalPipe,
        MatButtonModule,
        MatIconModule,
    ],
    templateUrl: './file-upload.component.html',
    styleUrls: [
        './file-upload.component.scss',
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true,
        },
    ],
})
export class FileUploadComponent
implements ControlValueAccessor, Validator {
  @ViewChild('fileInput', { static: true }) fileInput!: ElementRef<HTMLInputElement>;

  @Input() multiple = true;
  @Input() placeholder = 'Datei auswählen';
  @Input() validators: FileInputValidators | null = null;

  // Optional immediate feedback upward
  @Output() filesChange = new EventEmitter<File[]>();

  files: File[] = [];

  // ControlValueAccessor
  writeValue(value: File[] | null): void {
      this.files = value ? [
          ...value,
      ] : [];
  }

  registerOnChange(fn: (value: File[]) => void): void {
      this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
      if (this.fileInput?.nativeElement) {
          this.fileInput.nativeElement.disabled = isDisabled;
      }
  }

  // Validator
  validate(_control: AbstractControl): ValidationErrors | null {
      if (!this.validators) {
          return null;
      }

      const errors: any = {};
      const { allowedTypes, maxFileSizeBytes, maxTotalSizeBytes } = this.validators;

      if (allowedTypes && allowedTypes.length > 0) {
          const invalidTypeFiles = this.files.filter(
              f => !allowedTypes.includes(f.type),
          );
          if (invalidTypeFiles.length) {
              errors.invalidType = {
                  allowedTypes,
                  invalidFiles: invalidTypeFiles.map(f => f.name),
              };
          }
      }

      if (maxFileSizeBytes != null) {
          const tooLarge = this.files.filter(f => f.size > maxFileSizeBytes);
          if (tooLarge.length) {
              errors.maxFileSize = {
                  maxFileSizeBytes,
                  invalidFiles: tooLarge.map(f => f.name),
              };
          }
      }

      if (maxTotalSizeBytes != null) {
          const total = this.files.reduce((sum, f) => sum + f.size, 0);
          if (total > maxTotalSizeBytes) {
              errors.maxTotalSize = {
                  maxTotalSizeBytes,
                  totalSizeBytes: total,
              };
          }
      }

      return Object.keys(errors).length ? errors : null;
  }

  registerOnValidatorChange?(fn: () => void): void {
      this.onValidatorChange = fn;
  }

  // UI interactions
  triggerFileDialog(): void {
      this.fileInput.nativeElement.click();

      window.addEventListener('focus', () => {
          this.markTouched();
      }, { once: true });
  }

  onFilesSelected(event: Event): void {
      const input = event.target as HTMLInputElement;
      if (!input.files || input.files.length === 0) {
          return;
      }
      this.addFilesFromList(input.files);
      this.markTouched();
      input.value = '';
  }

  onDrop(event: DragEvent): void {
      event.preventDefault();
      event.stopPropagation();
      this.markTouched();
      const dt = event.dataTransfer;
      if (!dt || !dt.files || dt.files.length === 0) {
          return;
      }
      this.addFilesFromList(dt.files);
  }

  onDragOver(event: DragEvent): void {
      event.preventDefault();
      event.stopPropagation();
  }

  removeFile(index: number): void {
      this.files.splice(index, 1);
      if (!this.multiple && this.files.length === 0) {
      // In single mode, removing the only file clears completely
      }
      this.propagateChange();
  }

  clearAll(): void {
      this.files = [];
      this.propagateChange();
  }

  markTouched(): void {
      this.onTouched();
  }

  getFileDescription(file: File): string {
      return `${file.name}, ${Math.round(file.size / 1024)} kilobytes`;
  }

  private onChange: (value: File[]) => void = () => {
  };

  private onTouched: () => void = () => {
  };

  private onValidatorChange: () => void = () => {
  };

  private addFilesFromList(fileList: FileList): void {
      const newFiles = Array.from(fileList);

      if (!this.multiple) {
      // Single file mode: clear existing and only take first new file
          this.files = newFiles.slice(0, 1);
      } else {
      // Multiple mode: append new files (dedupe)
          const existingKeys = new Set(this.files.map(f => `${f.name}-${f.size}`));
          for (const f of newFiles) {
              const key = `${f.name}-${f.size}`;
              if (!existingKeys.has(key)) {
                  this.files.push(f);
              }
          }
      }

      this.propagateChange();
  }

  private propagateChange(): void {
      this.onChange(this.files);
      this.filesChange.emit(this.files);
      this.onValidatorChange();
  }
}
